// Giriş ekranı altındaki "Cleandy … Programı" adı: sektöre ve dile göre.
// Desteklenmeyen dilde İngilizce; bilinmeyen sektörde kuru temizleme.

export type Sector = 'dryCleaning' | 'carpetWashing' | 'sofaWashing' | 'carWash' | 'shoeShine' | 'tailoring' | 'barber';
type Lang = 'tr' | 'en' | 'ru' | 'de' | 'fr' | 'es' | 'ar' | 'it' | 'pt' | 'nl';

const NAMES: Record<Sector, Record<Lang, string>> = {
  dryCleaning: {
    tr: 'Cleandy Kuru Temizleme Programı', en: 'Cleandy Dry Cleaning Software', ru: 'Cleandy — программа для химчистки',
    de: 'Cleandy Software für Textilreinigung', fr: 'Cleandy Logiciel de pressing', es: 'Cleandy Software para tintorería',
    ar: 'Cleandy برنامج التنظيف الجاف', it: 'Cleandy Software per lavanderia', pt: 'Cleandy Software para lavandaria', nl: 'Cleandy Software voor stomerijen',
  },
  carpetWashing: {
    tr: 'Cleandy Halı Yıkama Programı', en: 'Cleandy Carpet Cleaning Software', ru: 'Cleandy — программа для чистки ковров',
    de: 'Cleandy Software für Teppichreinigung', fr: 'Cleandy Logiciel de nettoyage de tapis', es: 'Cleandy Software para lavado de alfombras',
    ar: 'Cleandy برنامج غسيل السجاد', it: 'Cleandy Software per lavaggio tappeti', pt: 'Cleandy Software para lavagem de tapetes', nl: 'Cleandy Software voor tapijtreiniging',
  },
  sofaWashing: {
    tr: 'Cleandy Koltuk Yıkama Programı', en: 'Cleandy Upholstery Cleaning Software', ru: 'Cleandy — программа для химчистки мебели',
    de: 'Cleandy Software für Polsterreinigung', fr: 'Cleandy Logiciel de nettoyage de canapés', es: 'Cleandy Software para limpieza de sofás',
    ar: 'Cleandy برنامج غسيل الكنب', it: 'Cleandy Software per lavaggio divani', pt: 'Cleandy Software para limpeza de sofás', nl: 'Cleandy Software voor meubelreiniging',
  },
  carWash: {
    tr: 'Cleandy Oto Yıkama Programı', en: 'Cleandy Car Wash Software', ru: 'Cleandy — программа для автомойки',
    de: 'Cleandy Software für Autowaschanlagen', fr: 'Cleandy Logiciel de lavage auto', es: 'Cleandy Software para lavado de coches',
    ar: 'Cleandy برنامج غسيل السيارات', it: 'Cleandy Software per autolavaggio', pt: 'Cleandy Software para lavagem de carros', nl: 'Cleandy Software voor autowasstraten',
  },
  shoeShine: {
    tr: 'Cleandy Lostra Programı', en: 'Cleandy Shoe Care Software', ru: 'Cleandy — программа для чистки обуви',
    de: 'Cleandy Software für Schuhpflege', fr: 'Cleandy Logiciel d’entretien de chaussures', es: 'Cleandy Software para limpieza de calzado',
    ar: 'Cleandy برنامج العناية بالأحذية', it: 'Cleandy Software per cura delle scarpe', pt: 'Cleandy Software para cuidado de calçado', nl: 'Cleandy Software voor schoenverzorging',
  },
  tailoring: {
    tr: 'Cleandy Terzi Programı', en: 'Cleandy Tailoring Software', ru: 'Cleandy — программа для ателье',
    de: 'Cleandy Software für Änderungsschneidereien', fr: 'Cleandy Logiciel de retouche', es: 'Cleandy Software para sastrería',
    ar: 'Cleandy برنامج الخياطة', it: 'Cleandy Software per sartoria', pt: 'Cleandy Software para costura e ajustes', nl: 'Cleandy Software voor kleermakerijen',
  },
  barber: {
    tr: 'Cleandy Berber Programı', en: 'Cleandy Barber Software', ru: 'Cleandy — программа для барбершопа',
    de: 'Cleandy Software für Friseure', fr: 'Cleandy Logiciel pour barbiers', es: 'Cleandy Software para barberías',
    ar: 'Cleandy برنامج صالونات الحلاقة', it: 'Cleandy Software per barbieri', pt: 'Cleandy Software para barbearias', nl: 'Cleandy Software voor kappers',
  },
};

// Sektör bilinmediğinde (ilk açılış, Google) kullanılan genel ad — 7 sektörün hepsini kapsar
const GENERIC: Record<Lang, string> = {
  tr: 'Cleandy – İşletme Yönetim Yazılımı', en: 'Cleandy – Business Management Software',
  ru: 'Cleandy — программа для управления бизнесом', de: 'Cleandy – Software für Betriebsverwaltung',
  fr: 'Cleandy – Logiciel de gestion d’entreprise', es: 'Cleandy – Software de gestión empresarial',
  ar: 'Cleandy – برنامج إدارة الأعمال', it: 'Cleandy – Software gestionale per attività',
  pt: 'Cleandy – Software de gestão empresarial', nl: 'Cleandy – Software voor bedrijfsbeheer',
};

/** Sayfa başlığı: sektör biliniyorsa "Cleandy Berber Programı", bilinmiyorsa genel ad */
export function pageTitle(sector: string | null | undefined, language: string): string {
  if (toSector(sector)) return softwareName(sector, language);
  return GENERIC[language as Lang] || GENERIC.en;
}

const SECTORS = Object.keys(NAMES) as Sector[];

export function toSector(value: string | null | undefined): Sector | null {
  return value && (SECTORS as string[]).includes(value) ? (value as Sector) : null;
}

export function softwareName(sector: string | null | undefined, language: string): string {
  const names = NAMES[toSector(sector) || 'dryCleaning'];
  return names[(language as Lang)] || names.en;
}

/** Giriş ekranında sektör: adres (?sector=) → bu cihazdaki firma ayarı → kayıtta seçilen → null */
export function detectSector(searchParam: string | null, storage: Pick<Storage, 'getItem'> | null): Sector | null {
  const fromUrl = toSector(searchParam);
  if (fromUrl) return fromUrl;
  if (!storage) return null;
  try {
    const raw = storage.getItem('customSettings');
    if (raw) {
      const s = JSON.parse(raw);
      const fromSettings = toSector(s?.default_service_type || s?.defaultServiceType);
      if (fromSettings) return fromSettings;
    }
  } catch { /* bozuk ayar: yoksay */ }
  try {
    return toSector(storage.getItem('pendingDefaultService'));
  } catch {
    return null;
  }
}
